import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { notifications, type Notification } from '@/services/notifications'

const TITLE_BASE = document.title

export function useNotifications() {
  const [unreadCount, setUnreadCount] = useState(0)
  const [items, setItems] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const [data, count] = await Promise.all([
        notifications.fetchNotifications(20),
        notifications.getUnreadCount(),
      ])
      setItems(data)
      setUnreadCount(count)
    } catch { /* silent */ }
    finally { setLoading(false) }
  }, [])

  const markRead = useCallback(async (id: string) => {
    await notifications.markRead(id)
    setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
    setUnreadCount(prev => Math.max(0, prev - 1))
  }, [])

  const markAllRead = useCallback(async () => {
    await notifications.markAllRead()
    setItems(prev => prev.map(n => ({ ...n, read: true })))
    setUnreadCount(0)
  }, [])

  // Realtime subscription for live notifications
  useEffect(() => {
    refresh()

    // Unique channel name per mount avoids StrictMode double-mount collision
    // where removing then re-creating the same channel name races with async cleanup.
    const name = `notifications-${crypto.randomUUID()}`
    const channel = supabase
      .channel(name)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications' },
        (payload) => {
          const row = payload.new as Notification
          setItems(prev => [row, ...prev])
          setUnreadCount(prev => prev + 1)
        },
      )
      .subscribe()

    channelRef.current = channel

    // Fallback polling every 60s
    const interval = setInterval(refresh, 60_000)

    return () => {
      supabase.removeChannel(channel)
      clearInterval(interval)
    }
  }, [refresh])

  // Document title badge
  useEffect(() => {
    if (unreadCount > 0) {
      document.title = `(${unreadCount}) ${TITLE_BASE}`
    } else {
      document.title = TITLE_BASE
    }
    return () => { document.title = TITLE_BASE }
  }, [unreadCount])

  return { unreadCount, items, loading, markRead, markAllRead, refresh }
}
